import { isEmpty } from 'radash';
import { useState } from 'react';

import { Button } from '@/components/core/Button';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import { Input } from '@/components/core/Input';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { logger } from '@/services/logger';

import { useUpdateBulkSubscription } from '../bulk-subscriptions.queries';
import { BulkSubscription } from '../bulk-subscriptions.types';

interface UpdateDetailsDialogProps {
    entity: BulkSubscription;
    open: boolean;
    onClose: (shouldRefetchData?: boolean) => void;
    onUpdated?: (entity: BulkSubscription) => void;
}

const UpdateDetailsDialog = ({
    entity,
    open,
    onClose,
    onUpdated,
}: UpdateDetailsDialogProps) => {

    const { toast } = useToast();
    const { t } = useTranslation();

    const [name, setName] = useState(entity.name);
    const [label, setLabel] = useState(entity.label);
    const [description, setDescription] = useState(entity.description);

    const { mutate: updateEntity, isPending } = useUpdateBulkSubscription({
        onError: (error) => {
            logger.error('UpdateDetailsDialog: Could not update bulk subscription entity', error);

            if (error.response.data.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error('ERROR_OCCURRED');
            }
        },
        onSuccess: () => {
            toast.success('BULK_SUBSCRIPTION.SAVED_SUCCESSFULLY');
            onUpdated?.({ ...entity, name, label, description });
            onClose(true);
        }
    });

    const onUpdateClick = () => {
        if (isEmpty(name)) {
            toast.error('BULK_SUBSCRIPTION.MISSING_REQUIRED_FIELDS');
            return;
        }

        updateEntity({
            ...entity,
            name,
            label,
            description,
        });
    };

    return (
        <Dialog open={open} onOpenChange={() => onClose()}>
            <DialogContent
                variant="small"
                onPointerDownOutside={() => onClose()}
                onInteractOutside={() => onClose()}
            >
                <DialogHeader>
                    <DialogHeaderDefault onClose={() => onClose()}>
                        {t('BULK_SUBSCRIPTION.RENAME')}
                    </DialogHeaderDefault>
                </DialogHeader>

                <form className="tw-p-4 tw-flex tw-flex-col" onSubmit={(e) => { e.preventDefault(); onUpdateClick(); }}>
                    <div className="tw-flex tw-justify-center tw-items-center tw-mb-3">
                        <label className="tw-ml-4 label-min-width tw-text-t-sm tw-max-w-[250px]">
                            {t('BULK_SUBSCRIPTION.NAME')}
                        </label>
                        <Input
                            placeholder={t('BULK_SUBSCRIPTION.NAME')}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </div>
                    <div className="tw-flex tw-justify-center tw-items-center tw-mb-3">
                        <label className="tw-ml-4 label-min-width tw-text-t-sm tw-max-w-[250px]">
                            {t('CUSTOMIZATION.LABEL')}
                        </label>
                        <Input
                            placeholder={t('CUSTOMIZATION.LABEL')}
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                        />
                    </div>
                    <div className="tw-flex tw-justify-center tw-items-center">
                        <label className="tw-ml-4 label-min-width tw-text-t-sm tw-max-w-[250px]">
                            {t('DATA_MODEL.DESCRIPTION')}
                        </label>
                        <Input
                            placeholder={t('DATA_MODEL.DESCRIPTION')}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </div>
                </form>
                <DialogFooter>
                    <Button
                        type="button"
                        disabled={isPending}
                        loading={isPending}
                        onClick={onUpdateClick}
                    >
                        {t('BULK_SUBSCRIPTION.SAVE_CHANGES')}
                    </Button>
                    <DialogClose asChild={true}>
                        <Button variant="secondary" onClick={() => onClose()} disabled={isPending}>
                            {t('CANCEL')}
                        </Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export { UpdateDetailsDialog };
